import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  LinearProgress,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  DataObject as CsvIcon,
  Article as TextIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { AgentService } from '../../services/api/agent';

interface BatchUploadDialogProps {
  open: boolean;
  onClose: () => void;
  conversationId?: string;
}

interface StagedFile {
  file: File;
  id: string;
}

interface BatchFileStatus {
  documentId: number;
  filename: string;
  mimetype: string;
  sizeBytes: number;
  status: string;
  progress: number;
  knowledgeGraphNodeId: string | null;
}

interface BatchStatus {
  batchId: string;
  status: string;
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  currentFileName: string | null;
  currentFileIndex: number;
  progress: number;
  files: BatchFileStatus[];
}

const MAX_FILES = 50;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_BATCH_SIZE = 500 * 1024 * 1024; // 500MB
const POLL_INTERVAL = 3000;

function getFileIcon(mimetype: string, filename: string) {
  if (mimetype === 'application/pdf') return <PdfIcon style={{ color: '#E94257' }} />;
  if (mimetype.includes('wordprocessingml') || mimetype.includes('msword')) return <WordIcon style={{ color: '#325BA8' }} />;
  if (mimetype.includes('spreadsheetml') || mimetype.includes('ms-excel')) return <ExcelIcon style={{ color: '#4DC4D2' }} />;
  if (mimetype === 'text/csv') return <CsvIcon style={{ color: '#4DC4D2' }} />;
  if (mimetype === 'text/plain' || mimetype === 'text/markdown' || mimetype === 'text/x-markdown') return <TextIcon style={{ color: '#999' }} />;
  const ext = filename.split('.').pop()?.toLowerCase();
  if (ext === 'md' || ext === 'markdown') return <TextIcon style={{ color: '#999' }} />;
  return <FileIcon style={{ color: '#999' }} />;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function FileStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon style={{ color: '#2e7d32' }} fontSize="small" />;
    case 'failed':
      return <ErrorIcon style={{ color: '#d32f2f' }} fontSize="small" />;
    case 'processing':
      return <CircularProgress size={18} />;
    default:
      return <PendingIcon style={{ color: 'rgba(0,0,0,0.38)' }} fontSize="small" />;
  }
}

const BatchUploadDialog: React.FC<BatchUploadDialogProps> = ({ open, onClose, conversationId }) => {
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [scope, setScope] = useState<'personal' | 'organization'>('personal');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const agentServiceRef = useRef(new AgentService());

  const isProcessing = batchStatus && !['completed', 'completed_with_errors', 'failed'].includes(batchStatus.status);
  const isDone = batchStatus && ['completed', 'completed_with_errors', 'failed'].includes(batchStatus.status);

  const totalSize = stagedFiles.reduce((sum, sf) => sum + sf.file.size, 0);

  // Poll for batch status
  const pollStatus = useCallback(async (batchId: string) => {
    try {
      const status = await agentServiceRef.current.getBatchUploadStatus(batchId);
      setBatchStatus(status);

      if (['completed', 'completed_with_errors', 'failed'].includes(status.status)) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Failed to poll batch status:', err);
    }
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      // Only reset if processing is done
      if (!isProcessing) {
        setStagedFiles([]);
        setBatchStatus(null);
        setError(null);
        setUploading(false);
        setUploadProgress(0);
      }
    }
  }, [open, isProcessing]);

  const handleAddFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = event.target.files;
    if (!newFiles) return;

    const filesToAdd: StagedFile[] = [];
    const errors: string[] = [];

    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];

      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: exceeds 100MB limit`);
        continue;
      }

      // Check for duplicates by name
      const isDuplicate = stagedFiles.some(sf => sf.file.name === file.name && sf.file.size === file.size);
      if (isDuplicate) {
        continue; // Silently skip duplicates
      }

      filesToAdd.push({
        file,
        id: `${file.name}-${file.size}-${Date.now()}-${i}`,
      });
    }

    const combinedFiles = [...stagedFiles, ...filesToAdd];
    if (combinedFiles.length > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} files per batch. Only the first ${MAX_FILES} were kept.`);
      setStagedFiles(combinedFiles.slice(0, MAX_FILES));
    } else {
      setStagedFiles(combinedFiles);
    }

    if (errors.length > 0) {
      setError(errors.join('; '));
    } else {
      setError(null);
    }

    // Reset file input so same files can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (id: string) => {
    setStagedFiles(prev => prev.filter(sf => sf.id !== id));
    setError(null);
  };

  const handleSubmit = async () => {
    if (stagedFiles.length === 0) return;

    if (totalSize > MAX_BATCH_SIZE) {
      setError(`Total batch size ${formatBytes(totalSize)} exceeds the 500MB limit`);
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await agentServiceRef.current.uploadDocumentBatch(
        stagedFiles.map(sf => sf.file),
        { scope, conversationId },
        (progress) => setUploadProgress(progress)
      );

      setUploading(false);

      // Initialize batch status from the upload response
      setBatchStatus({
        batchId: result.batchId,
        status: 'pending',
        totalFiles: result.totalFiles,
        completedFiles: 0,
        failedFiles: 0,
        currentFileName: null,
        currentFileIndex: 0,
        progress: 0,
        files: result.files.map(f => ({
          ...f,
          progress: 0,
          knowledgeGraphNodeId: null,
        })),
      });

      // Start polling
      pollIntervalRef.current = setInterval(() => pollStatus(result.batchId), POLL_INTERVAL);
      // Also poll immediately
      pollStatus(result.batchId);
    } catch (err: any) {
      setUploading(false);
      setError(err?.response?.data?.error || err?.message || 'Failed to start batch upload');
    }
  };

  const handleClose = () => {
    if (uploading) return; // Don't close during upload
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { maxHeight: '80vh' } }}
    >
      <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CloudUploadIcon color="primary" />
          <Typography variant="h6">Batch Document Upload</Typography>
        </Stack>
        <IconButton size="small" onClick={handleClose} disabled={uploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} style={{ marginBottom: 16 }}>
            {error}
          </Alert>
        )}

        {/* === FILE STAGING VIEW === */}
        {!batchStatus && (
          <>
            <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
              Select documents to populate your knowledge graph. Files will be processed
              in the background with full topic and semantic extraction.
            </Typography>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.markdown,.ppt,.pptx"
            />

            {/* Add Files button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddFiles}
              disabled={uploading || stagedFiles.length >= MAX_FILES}
              fullWidth
              style={{ marginBottom: 16 }}
            >
              Add Files
            </Button>

            {/* File list */}
            {stagedFiles.length > 0 && (
              <>
                <List dense style={{ maxHeight: 300, overflow: 'auto' }}>
                  {stagedFiles.map((sf) => (
                    <ListItem key={sf.id} style={{ paddingRight: 48 }}>
                      <ListItemIcon style={{ minWidth: 36 }}>
                        {getFileIcon(sf.file.type, sf.file.name)}
                      </ListItemIcon>
                      <ListItemText
                        primary={sf.file.name}
                        secondary={formatBytes(sf.file.size)}
                        primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" size="small" onClick={() => handleRemoveFile(sf.id)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>

                <Divider style={{ marginTop: 8, marginBottom: 8 }} />

                {/* Summary */}
                <Stack direction="row" justifyContent="space-between" style={{ marginBottom: 16 }}>
                  <Typography variant="caption" color="text.secondary">
                    {stagedFiles.length} file{stagedFiles.length !== 1 ? 's' : ''} selected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total: {formatBytes(totalSize)}
                  </Typography>
                </Stack>
              </>
            )}

            {stagedFiles.length === 0 && (
              <div style={{ textAlign: 'center', paddingTop: 32, paddingBottom: 32, color: 'rgba(0,0,0,0.38)' }}>
                <CloudUploadIcon style={{ fontSize: 48, marginBottom: 8 }} />
                <Typography variant="body2">
                  Click "Add Files" to select documents.
                  <br />
                  You can add files from multiple folders.
                </Typography>
              </div>
            )}

            {/* Scope selection */}
            {stagedFiles.length > 0 && (
              <FormControl component="fieldset" style={{ marginBottom: 8 }}>
                <FormLabel component="legend" style={{ fontSize: '0.875rem' }}>
                  Knowledge Graph Scope
                </FormLabel>
                <RadioGroup
                  row
                  value={scope}
                  onChange={(e) => setScope(e.target.value as 'personal' | 'organization')}
                >
                  <FormControlLabel value="personal" control={<Radio size="small" />} label="Personal" />
                  <FormControlLabel value="organization" control={<Radio size="small" />} label="Organization" />
                </RadioGroup>
              </FormControl>
            )}

            {/* Upload progress bar (during HTTP upload, before processing starts) */}
            {uploading && (
              <div style={{ marginTop: 16 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Uploading files...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </div>
            )}
          </>
        )}

        {/* === PROCESSING PROGRESS VIEW === */}
        {batchStatus && (
          <>
            {/* Overall progress */}
            <div style={{ marginBottom: 16 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" style={{ marginBottom: 4 }}>
                <Typography variant="body2" fontWeight="medium">
                  {isDone
                    ? batchStatus.status === 'completed'
                      ? 'All files processed successfully'
                      : batchStatus.status === 'failed'
                        ? 'Processing failed'
                        : 'Processing completed with some errors'
                    : batchStatus.currentFileName
                      ? `Processing: ${batchStatus.currentFileName}`
                      : 'Starting...'
                  }
                </Typography>
                <Chip
                  label={`${batchStatus.completedFiles + batchStatus.failedFiles} / ${batchStatus.totalFiles}`}
                  size="small"
                  color={isDone ? (batchStatus.failedFiles > 0 ? 'warning' : 'success') : 'primary'}
                />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={batchStatus.progress}
                color={isDone ? (batchStatus.failedFiles > 0 ? 'warning' : 'success') : 'primary'}
                style={{ height: 8, borderRadius: 4 }}
              />
            </div>

            {/* Summary chips */}
            {isDone && (
              <Stack direction="row" spacing={1} style={{ marginBottom: 16 }}>
                {batchStatus.completedFiles > 0 && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${batchStatus.completedFiles} succeeded`}
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                )}
                {batchStatus.failedFiles > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${batchStatus.failedFiles} failed`}
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            )}

            {/* Per-file status list */}
            <List dense style={{ maxHeight: 350, overflow: 'auto' }}>
              {batchStatus.files.map((file) => (
                <ListItem key={file.documentId}>
                  <ListItemIcon style={{ minWidth: 36 }}>
                    <FileStatusIcon status={file.status} />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.filename}
                    secondary={formatBytes(file.sizeBytes)}
                    primaryTypographyProps={{
                      noWrap: true,
                      variant: 'body2',
                      color: file.status === 'failed' ? 'error.main' : 'text.primary',
                    }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <Tooltip title={file.status}>
                    <Typography variant="caption" color="text.secondary" style={{ marginLeft: 8 }}>
                      {file.status === 'completed' ? 'Done' : file.status === 'failed' ? 'Failed' : file.status === 'processing' ? 'Processing...' : 'Waiting'}
                    </Typography>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!batchStatus && (
          <>
            <Button onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={stagedFiles.length === 0 || uploading}
              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            >
              {uploading ? 'Uploading...' : `Upload & Process ${stagedFiles.length} File${stagedFiles.length !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
        {batchStatus && (
          <Button
            variant={isDone ? 'contained' : 'outlined'}
            onClick={handleClose}
            disabled={uploading}
          >
            {isDone ? 'Close' : 'Close (processing continues)'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchUploadDialog;
